import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { 
  DocumentIcon,
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { GlobalWorkerOptions } from 'pdfjs-dist';

interface DocumentType {
  id: number;
  description: string;
  category_name: string;
  is_optional: boolean;
}

interface DogDocument {
  id: number;
  filename: string;
  description: string;
  category: string;
  upload_date: string;
  doc_type_id?: number;
}

interface MissingDocument {
  doc_type_id: number;
  description: string;
}

interface MyDogDocumentsProps {
  dogId: string;
}

// PDF.js Worker-Setup
const PDFJS_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const MyDogDocuments: React.FC<MyDogDocumentsProps> = ({ dogId }) => {
  const { config } = useContext(AuthContext);
  const [documents, setDocuments] = useState<DogDocument[]>([]);
  const [missingDocs, setMissingDocs] = useState<MissingDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<DogDocument | null>(null);
  const [docUrlForPreview, setDocUrlForPreview] = useState<string | null>(null);
  const [docErrorForPreview, setDocErrorForPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'warning' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'two' as 'single' | 'two',
    onConfirm: () => {},
  });

  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  // PDF.js Worker konfigurieren
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
  }, []);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadDocumentTypes = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/dogs/dog_document_types');
      console.log('Document types response:', res.data);
      
      if (res.data.success) {
        const rawTypes = res.data.documentTypes || [];
        const validTypes = rawTypes.filter((dt: any) => 
          dt && typeof dt.description === 'string' && dt.description.trim() !== ''
        );
        validTypes.sort((a: DocumentType, b: DocumentType) => {
          if (a.is_optional === b.is_optional) return a.description.localeCompare(b.description);
          return a.is_optional ? 1 : -1;
        });
        console.log('Valid document types:', validTypes);
        setDocumentTypes(validTypes);
      } else {
        setError(res.data.message || 'Fehler beim Laden der Dokumenttypen');
        setDocumentTypes([]);
      }
    } catch (err) {
      console.error('Error loading document types:', err);
      setError('Netzwerkfehler beim Laden der Dokumenttypen');
      setDocumentTypes([]);
    }
  }, []);

  const loadDogDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/dogs/get_dog_documents/${dogId}`);
      if (res.data.success) {
        setDocuments(res.data.documents || []);
      } else {
        setError(res.data.message || 'Fehler beim Laden der Dokumente');
        setDocuments([]);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Netzwerkfehler beim Laden der Dokumente');
      }
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [dogId]);

  const checkRequiredDocuments = useCallback(async () => {
    try {
      const res = await apiClient.get(`/api/dogs/check_required_documents/${dogId}`);
      setMissingDocs(!res.data.success && res.data.missing_documents ? res.data.missing_documents : []);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        setMissingDocs([]);
      }
    }
  }, [dogId]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await loadDocumentTypes();
      await loadDogDocuments();
      await checkRequiredDocuments();
      setIsLoading(false);
    };
    loadData();
  }, [loadDocumentTypes, loadDogDocuments, checkRequiredDocuments]);

  // Cleanup: Re-enable scrolling when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleDeleteDocument = (docId: number, docName: string) => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Dokument löschen',
      message: `Möchtest du dieses Dokument "${docName}" wirklich endgültig löschen?`,
      variant: 'two',
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/api/dogs/delete_dog_document/${dogId}/${docId}`);
          if (res.data.success) {
            await Promise.all([loadDogDocuments(), checkRequiredDocuments()]);
            showNotification('Das Dokument wurde erfolgreich gelöscht', 'success');
          } else {
            setError(res.data.message || 'Fehler beim Löschen des Dokuments');
          }
        } catch (err: any) {
          if (err.response?.status !== 401) {
            setError('Netzwerkfehler beim Löschen des Dokuments');
          }
        }
      },
    });
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const getOrderedGroupedDocuments = useCallback(() => {
    const categoryOrder = documentTypes.map(dt => dt.category_name).filter(name => name);
    const grouped = documents.reduce<Record<string, DogDocument[]>>((acc, doc) => {
      const categoryKey = (doc.category && typeof doc.category === 'string' && doc.category.trim() !== '') 
        ? doc.category 
        : 'Sonstige Dokumente';
      if (!acc[categoryKey]) acc[categoryKey] = [];
      acc[categoryKey].push(doc);
      return acc;
    }, {});

    categoryOrder.forEach(catName => {
      if (!grouped[catName]) grouped[catName] = [];
    });

    const result = categoryOrder.map(catName => ({
      categoryName: catName,
      docs: grouped[catName] || []
    }));

    Object.keys(grouped).forEach(catName => {
      if (!categoryOrder.includes(catName)) {
        result.push({ categoryName: catName, docs: grouped[catName] });
      }
    });

    return result.filter(group => 
      !(group.categoryName === 'Sonstige Dokumente' && 
        group.docs.length === 0 && 
        !categoryOrder.includes('Sonstige Dokumente'))
    );
  }, [documents, documentTypes]);

  const loadDocumentForPreview = async (doc: DogDocument) => {
    setDocUrlForPreview(null);
    setDocErrorForPreview(null);
    
    try {
      const response = await apiClient.get(`/api/dogs/documents/download/${doc.id}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf, image/*'
        }
      });

      const blob = response.data;
      const mimeType = response.headers['content-type'] || 
        (doc.filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
      const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
      setDocUrlForPreview(blobUrl);
    } catch (error: any) {
      if (error.response?.status !== 401) {
        let errorMsg = `HTTP-Fehler ${error.response?.status || 'Unbekannt'}`;
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = error.message;
        }
        setDocErrorForPreview(`Vorschau konnte nicht geladen werden: ${errorMsg}`);
      }
    }
  };

  const handlePreview = async (doc: DogDocument) => {
    setSelectedDoc(doc);
    await loadDocumentForPreview(doc);
    setShowPreviewModal(true);
    
    // Disable scrolling when modal opens
    document.body.style.overflow = 'hidden';
  };

  const handleClosePreviewModal = () => {
    if (docUrlForPreview && docUrlForPreview.startsWith('blob:')) {
      URL.revokeObjectURL(docUrlForPreview);
    }
    setDocUrlForPreview(null);
    setDocErrorForPreview(null);
    setShowPreviewModal(false);
    setSelectedDoc(null);
    
    // Re-enable scrolling
    document.body.style.overflow = '';
  };

  const getFileIcon = (filename: string) => {
    if (!filename) return <DocumentIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />;
    const extension = filename.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <DocumentTextIcon className="h-6 w-6 text-error-500 dark:text-error-400" />;
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return <PhotoIcon className="h-6 w-6 text-info-500 dark:text-info-400" />;
    }
    return <DocumentIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Datum unbekannt';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  const orderedGroupedDocs = getOrderedGroupedDocuments();

  if (isLoading && !documents.length) {
    return (
      <div className="text-center py-6">
        <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
        <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">Lade Dokumente…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Notification */}
      {notification && (
        <div
          className={`p-3 rounded-2xl flex items-center gap-2 backdrop-blur ${
            notification.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 border border-emerald-500/30'
              : notification.type === 'error'
              ? 'bg-rose-500/10 text-rose-700 dark:text-rose-200 border border-rose-500/30'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-200 border border-blue-500/30'
          }`}
        >
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">{notification.message}</span>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-200 px-4 py-2.5 rounded-2xl text-sm flex items-center gap-2">
          <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Missing Documents Warning */}
      {missingDocs.length > 0 && (
        <div
          className="px-4 py-3 rounded-2xl flex items-start gap-3 bg-amber-500/5 border border-amber-500/30"
        >
          <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
              Fehlende Pflichtdokumente
            </div>
            <ul className="mt-1 space-y-0.5 text-sm text-gray-700 dark:text-slate-300">
              {missingDocs.map((doc) => (
                <li key={doc.doc_type_id} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-amber-400" />
                  <span>{doc.description}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Documents List */}
      <div className="space-y-2.5">
        {orderedGroupedDocs.length === 0 ? (
          <div
            className="px-4 py-6 rounded-2xl text-center bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
          >
            <DocumentIcon className="h-8 w-8 text-gray-400 dark:text-slate-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Bisher keine Dokumente für diesen Hund hochgeladen.
            </p>
          </div>
        ) : (
          orderedGroupedDocs.map(({ categoryName, docs }) => (
            <div
              key={categoryName}
              className="rounded-2xl overflow-hidden bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700/60"
            >
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryName)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-white/[0.025] transition"
              >
                <span
                  className="truncate text-sm font-bold text-gray-900 dark:text-white"
                  title={categoryName || 'Unbekannte Kategorie'}
                >
                  {categoryName || 'Unbekannte Kategorie'}
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                      docs.length > 0
                        ? 'bg-primary-500 text-slate-950'
                        : 'bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-slate-300'
                    }`}
                  >
                    {docs.length}
                  </span>
                  {expandedCategories[categoryName] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                  )}
                </div>
              </button>

              {/* Document Items */}
              {expandedCategories[categoryName] && (
                <div
                  className="p-2.5 space-y-2 border-t border-gray-200 dark:border-slate-800"
                >
                  {docs.length > 0 ? (
                    docs.map((doc) => (
                      <div
                        key={doc.id}
                        className="px-3 py-2.5 rounded-xl flex justify-between items-center gap-3 transition bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700/50"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.borderColor = 'rgba(16,185,129,0.30)')
                        }
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '';
                        }}
                      >
                        <div className="flex items-center gap-3 flex-grow overflow-hidden">
                          <div className="w-10 h-10 rounded-xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center shrink-0">
                            {getFileIcon(doc.filename)}
                          </div>
                          <div className="flex-grow overflow-hidden">
                            <p className="font-bold text-gray-900 dark:text-white text-sm truncate">
                              {doc.description === 'Keine Angaben'
                                ? doc.filename
                                : doc.description}
                            </p>
                            <p className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5">
                              Hochgeladen: {formatDate(doc.upload_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handlePreview(doc)}
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition"
                            title="Vorschau"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <a
                            href={`${BASE_URL}/api/dogs/documents/download/${doc.id}`}
                            download
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition"
                            title="Herunterladen"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </a>
                          <button
                            onClick={() =>
                              handleDeleteDocument(
                                doc.id,
                                doc.description === 'Keine Angaben'
                                  ? doc.filename
                                  : doc.description
                              )
                            }
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 dark:text-slate-500 hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400 transition"
                            title="Löschen"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-slate-400 px-2 py-4 text-center">
                      Keine Dokumente in dieser Kategorie.
                    </p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Upload Button */}
      <button
        onClick={() => {
          if (documentTypes.length === 0) {
            showNotification(
              'Keine Dokumenttypen verfügbar. Bitte wenden Sie sich an den Administrator.',
              'error'
            );
            return;
          }
          setShowUploadModal(true);
        }}
        className="w-full px-4 py-3 rounded-2xl border border-dashed border-primary-500/40 bg-primary-500/5 text-primary-700 dark:text-primary-300 hover:bg-primary-500/10 text-sm font-bold transition flex items-center justify-center gap-2"
      >
        <DocumentArrowUpIcon className="h-4 w-4" />
        Neues Dokument hochladen
      </button>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadDocumentModal
          dogId={dogId}
          onClose={() => setShowUploadModal(false)}
          onUpload={(successMessage) => {
            loadDogDocuments();
            checkRequiredDocuments();
            if (successMessage) {
              showNotification(successMessage, 'success');
            }
          }}
          documentTypes={documentTypes}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDoc && (
        <PreviewModal
          doc={selectedDoc}
          docUrl={docUrlForPreview}
          docError={docErrorForPreview}
          onClose={handleClosePreviewModal}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        }}
      />
    </div>
  );
};

// Upload Document Modal Component
interface UploadDocumentModalProps {
  dogId: string;
  onClose: () => void;
  onUpload: (message?: string) => void;
  documentTypes: DocumentType[];
}

const UploadDocumentModal: React.FC<UploadDocumentModalProps> = ({ 
  dogId, 
  onClose, 
  onUpload, 
  documentTypes 
}) => {
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async () => {
    console.log('Upload attempt:', { categoryId, file, description });
    
    if (!categoryId || !file) {
      setError('Bitte Kategorie und Datei auswählen.');
      return;
    }

    setError(null);
    setIsUploading(true);
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('category_id', categoryId);
    formData.append('description', description.trim() || file.name);

    try {
      const res = await apiClient.post(`/api/dogs/upload_dog_document/${dogId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        const uploadedFileName = description.trim() || file.name;
        onUpload(`Das Dokument "${uploadedFileName}" wurde erfolgreich hochgeladen.`);
        onClose();
      } else {
        setError(res.data.message || 'Fehler beim Hochladen des Dokuments.');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError('Netzwerkfehler: Dokument konnte nicht hochgeladen werden.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const inputClass =
    'mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition';
  const inputStyle = {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid #334155',
    colorScheme: 'dark' as const,
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg flex flex-col overflow-hidden"
        style={{
          background: '#1c2432',
          border: '1px solid #334155',
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold">
              Dokumente
            </div>
            <h5 className="text-2xl font-black text-white tracking-tight mt-1">
              Hochladen
            </h5>
            <p className="text-slate-400 text-sm mt-1">PDF, Bilder oder DOC · max. 10 MB</p>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="w-9 h-9 rounded-full flex items-center justify-center transition disabled:opacity-50"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
            aria-label="Schließen"
          >
            <XMarkIcon className="h-4 w-4 text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 px-3 py-2 rounded-xl text-sm flex items-center gap-2">
              <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Kategorie *
            </span>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              disabled={isUploading}
              className={inputClass}
              style={inputStyle}
            >
              <option value="">Kategorie auswählen…</option>
              {documentTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.description} {type.is_optional ? '(Optional)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Beschreibung (optional)
            </span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="z. B. Impfpass Seite 3"
              disabled={isUploading}
              className={inputClass}
              style={inputStyle}
            />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Datei auswählen *
            </span>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
              disabled={isUploading}
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx"
              className="mt-1 w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-primary-500/15 file:text-primary-300 hover:file:bg-primary-500/25 transition"
            />
            {file && (
              <p className="text-[11px] text-slate-400 mt-2">Ausgewählt: {file.name}</p>
            )}
          </label>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
          style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
        >
          <button
            onClick={onClose}
            disabled={isUploading}
            className="px-4 py-3 rounded-xl text-slate-200 text-sm font-semibold transition disabled:opacity-50"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleUpload}
            disabled={!categoryId || !file || isUploading}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-slate-950 border-t-transparent rounded-full" />
                Lädt hoch…
              </>
            ) : (
              'Hochladen'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Preview Modal Component
interface PreviewModalProps {
  doc: DogDocument;
  docUrl: string | null;
  docError: string | null;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ doc, docUrl, docError, onClose }) => {
  const isPdf = doc.filename.toLowerCase().endsWith('.pdf');
  
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Datum unbekannt';
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return 'Ungültiges Datum';
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl flex flex-col overflow-hidden"
        style={{
          background: '#1c2432',
          border: '1px solid #334155',
          boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
          maxHeight: 'calc(100vh - 32px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800 gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                isPdf
                  ? 'bg-rose-500/10 border border-rose-500/30'
                  : 'bg-blue-500/10 border border-blue-500/30'
              }`}
            >
              {isPdf ? (
                <DocumentTextIcon className="h-5 w-5 text-rose-300" />
              ) : (
                <PhotoIcon className="h-5 w-5 text-blue-300" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-primary-300 font-bold">
                Vorschau
              </div>
              <h5 className="text-lg font-black text-white tracking-tight truncate">
                {doc.description === 'Keine Angaben' ? doc.filename : doc.description}
              </h5>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
            aria-label="Schließen"
          >
            <XMarkIcon className="h-4 w-4 text-slate-300" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-auto p-4" style={{ background: 'rgba(15,23,42,0.6)' }}>
          {docUrl ? (
            isPdf ? (
              <div className="w-full h-full rounded-xl overflow-hidden" style={{ background: '#1e293b' }}>
                <Worker workerUrl={PDFJS_WORKER_URL}>
                  <div
                    className="w-full h-full overflow-auto"
                    style={{
                      height: '60vh',
                      minHeight: '500px',
                      maxHeight: '75vh',
                    }}
                  >
                    <Viewer fileUrl={docUrl} defaultScale={SpecialZoomLevel.PageWidth} />
                  </div>
                </Worker>
              </div>
            ) : (
              <div className="flex justify-center items-center w-full h-full">
                <img
                  src={docUrl}
                  alt={`Vorschau von ${doc.filename}`}
                  className="max-h-full max-w-full object-contain rounded-xl"
                />
              </div>
            )
          ) : docError ? (
            <div className="h-full flex flex-col justify-center items-center text-center py-12">
              <ExclamationCircleIcon className="h-12 w-12 text-rose-400 mb-3" />
              <p className="font-bold text-lg text-white">Fehler beim Laden</p>
              <p className="text-sm text-slate-400 mt-1">{docError}</p>
            </div>
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center py-12">
              <div className="animate-spin h-10 w-10 border-4 border-primary-500 border-t-transparent rounded-full mb-3" />
              <p className="text-slate-400">Dokumentenvorschau wird geladen…</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-4 flex justify-between items-center gap-3"
          style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
        >
          <p className="text-[11px] text-slate-400 truncate">
            {doc.filename} · {formatDate(doc.upload_date)}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-200 text-sm font-semibold transition shrink-0"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MyDogDocuments;